import { apiClient } from '../client/apiClient';
import type {
	IBaseResponse,
	IPaginatedResponse,
	IPagination,
} from '../types/common';
import type { IConversation } from '../types/conversation';
import type { IUser, IUserProfile } from '../types/user';

export const userService = {
	/**
	 * Get current user profile
	 */
	getProfile: () => apiClient.get<IBaseResponse<IUser>>('/users/profile'),

	/**
	 * Get current user info
	 */
	getUserInfo: () => apiClient.get<IBaseResponse<IUser>>('/users/info'),

	/**
	 * Update user profile
	 */
	updateProfile: (data: IUserProfile) =>
		apiClient.put<IBaseResponse<void>>('/users/profile/update', data),

	/**
	 * Get specific user profile
	 */
	getUserProfile: (userId: string) =>
		apiClient.get<IBaseResponse<IUser>>(`/users/${userId}/profile`),

	/**
	 * Get user followers
	 */
	getFollowers: (userId: string, params: IPagination) =>
		apiClient.get<IPaginatedResponse<IUser>>(`/users/${userId}/followers`, {
			params,
		}),

	/**
	 * Get user following
	 */
	getFollowing: (userId: string, params: IPagination) =>
		apiClient.get<IPaginatedResponse<IUser>>(`/users/${userId}/following`, {
			params,
		}),

	/**
	 * Get user conversations
	 */
	getUserConversations: (userId: string, params: IPagination) =>
		apiClient.get<IPaginatedResponse<IConversation>>(
			`/users/${userId}/conversations`,
			{ params },
		),

	/**
	 * Get related users
	 */
	getRelatedUsers: (userId: string, params: IPagination) =>
		apiClient.get<IPaginatedResponse<IUser>>(`/users/${userId}/related`, {
			params,
		}),

	/**
	 * Get user follow-up questions
	 */
	getFollowUpQuestions: (params: IPagination) =>
		apiClient.get<IPaginatedResponse<string>>('/users/follow-up-questions', {
			params,
		}),

	/**
	 * Follow user
	 */
	followUser: (userId: string) =>
		apiClient.post<IBaseResponse<{ count: number }>>(`/users/${userId}/follow`),

	/**
	 * Unfollow user
	 */
	unfollowUser: (userId: string) =>
		apiClient.post<IBaseResponse<{ count: number }>>(
			`/users/${userId}/unfollow`,
		),

	/**
	 * Block user
	 */
	blockUser: (userId: string) =>
		apiClient.post<IBaseResponse<void>>(`/users/${userId}/block`),

	/**
	 * Unblock user
	 */
	unblockUser: (userId: string) =>
		apiClient.post<IBaseResponse<void>>(`/users/${userId}/unblock`),

	/**
	 * Report user
	 */
	reportUser: (userId: string, reasons: string[]) =>
		apiClient.post<IBaseResponse<void>>(`/users/${userId}/report`, {
			reasons,
		}),

	/**
	 * Delete current user account
	 */
	deleteUser: () => apiClient.delete<IBaseResponse<void>>('/users/delete'),
};
